"use client";

import { useSyncExternalStore } from "react";
import type { MouseEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormattedMessage } from "react-intl";
import { FaArrowLeft } from "react-icons/fa6";
import type { PostSummary } from "@/lib/ressources";
import { categoryToSlug } from "@/lib/category-slugs";
import { CATEGORY_META } from "./category-meta";
import { DraftBadge } from "./DraftBadge";
import { TagLabel } from "./TagLabel";
import { getLastListingPath, getPreviousPath } from "./nav-history";

// Nothing to subscribe to: sessionStorage is read once, client-side only.
const subscribeToNothing = () => () => {};

function formatDate(date: string, locale: "fr" | "en"): string {
  const intlLocale = locale === "en" ? "en-US" : "fr-FR";
  return new Intl.DateTimeFormat(intlLocale, { dateStyle: "long" }).format(new Date(date));
}

export function ArticleHeader({
  post,
  locale = "fr",
}: {
  post: PostSummary;
  locale?: "fr" | "en";
}) {
  const meta = CATEGORY_META[post.category];
  const langSuffix = locale === "en" ? "/en" : "";
  const categoryHref = `/resources/${categoryToSlug(post.category)}${langSuffix}`;
  const mainHref = `/resources${langSuffix}`;

  // The article can be reached either from the main resources page or from
  // its category page — the back arrow should return wherever the visitor
  // actually came from. document.referrer doesn't work here: Next.js
  // navigates client-side, so it never updates past the tab's first hard
  // load. ResourcesNavTracker records the last listing page visited instead.
  const backHref = useSyncExternalStore(
    subscribeToNothing,
    () => (getLastListingPath() === mainHref ? mainHref : categoryHref),
    () => categoryHref,
  );
  const router = useRouter();

  // When the listing is the page just before this one, go back in history rather than
  // pushing it again: the listing then reopens where the visitor left it (scroll,
  // revealed batches, filter) instead of at the top. Modified clicks keep the link.
  const onBackClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (getPreviousPath() !== backHref) return;
    e.preventDefault();
    router.back();
  };

  return (
    <header className="mx-auto max-w-5xl px-6 pt-32 pb-8 sm:pt-40 2xl:max-w-6xl">
      <Link
        href={backHref}
        onClick={onBackClick}
        className="-mt-3 mb-3 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brand hover:text-brand-ink"
      >
        <FaArrowLeft className="text-xs" aria-hidden="true" />
        <FormattedMessage id="ressources.backToCategory" />
      </Link>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <DraftBadge draft={post.draft} />
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${meta.color} ${meta.bg}`}
        >
          <FormattedMessage id={meta.labelKey} />
        </span>
        {post.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full bg-gray-ulight px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-main"
          >
            <TagLabel tag={tag} />
          </span>
        ))}
      </div>

      <h1 className="text-3xl font-bold leading-tight text-text-main sm:text-4xl 2xl:text-5xl">{post.title}</h1>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-main 2xl:text-base">
        {post.author && <span>{post.author}</span>}
        {post.author && <span aria-hidden="true">·</span>}
        <time dateTime={post.date}>
          <FormattedMessage
            id="ressources.publishedOn"
            values={{ date: formatDate(post.date, locale) }}
          />
        </time>
        <span aria-hidden="true">·</span>
        <span>
          <FormattedMessage
            id="ressources.readingTime"
            values={{ minutes: post.readingTimeMinutes }}
          />
        </span>
      </div>
    </header>
  );
}
